import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailPlus, Trash2, MailCheck } from "lucide-react";
import { API } from "@/App";

export default function AdminNotifications({ headers }) {
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const fetchEmails = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notification-emails`, { headers });
      setEmails(data);
    } catch {
      toast.error("Impossible de charger les emails de notification.");
    }
  }, [headers]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  const add = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/notification-emails`, { email: newEmail.trim() }, { headers });
      toast.success(`${newEmail.trim()} recevra les nouvelles réservations.`);
      setNewEmail("");
      fetchEmails();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible d'ajouter cet email.");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (item) => {
    try {
      await axios.delete(`${API}/admin/notification-emails/${item.id}`, { headers });
      setEmails((list) => list.filter((x) => x.id !== item.id));
      toast.success(`${item.email} ne recevra plus les notifications.`);
    } catch {
      toast.error("Impossible de retirer cet email.");
    }
  };

  return (
    <section className="mt-14" data-testid="admin-notifications-section">
      <h2 className="font-serif text-2xl font-semibold text-stone-900">Notifications email</h2>
      <p className="mt-1 text-sm text-stone-500">
        Ces adresses reçoivent un email à chaque nouvelle réservation.
      </p>

      <form onSubmit={add} className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4">
        <input
          type="email"
          required
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="employe@exemple.fr"
          className="min-w-48 flex-1 rounded-xl border border-stone-200 bg-cream/60 px-4 py-2.5 text-sm outline-none transition-all placeholder:text-stone-400 focus:border-burgundy focus:ring-2 focus:ring-burgundy/15"
          data-testid="notif-email-input"
        />
        <button
          type="submit"
          disabled={adding}
          className="flex items-center gap-2 rounded-full bg-burgundy px-5 py-2.5 text-xs font-semibold text-cream transition-all hover:bg-burgundy-dark disabled:opacity-60"
          data-testid="notif-email-add-button"
        >
          <MailPlus className="h-4 w-4" />
          {adding ? "Ajout…" : "Ajouter cet email"}
        </button>
      </form>

      <div className="mt-4 space-y-2" data-testid="notif-email-list">
        {emails.length === 0 && (
          <p className="rounded-xl border border-dashed border-stone-300 bg-white p-6 text-center text-sm text-stone-500" data-testid="notif-email-empty">
            Aucune adresse pour le moment — ajoutez-en une pour être prévenu des nouvelles réservations.
          </p>
        )}
        {emails.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3"
            data-testid={`notif-email-${item.id}`}
          >
            <span className="flex items-center gap-2 text-sm text-stone-700">
              <MailCheck className="h-4 w-4 text-sage" />
              {item.email}
            </span>
            <button
              onClick={() => remove(item)}
              aria-label={`Retirer ${item.email}`}
              className="rounded-full border border-red-200 p-2 text-red-700 transition-colors hover:bg-red-50"
              data-testid={`delete-notif-email-${item.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
